import { useState } from "react";
import { Calendar } from "lucide-react";

import { useLocalization } from "@i18n";

import {
  useSaveInspectionMutation,
  type InspectionDetail,
  type InspectionParticipant,
} from "../../../api/inspection";
import { toInspectionDateInputValue } from "../../../utils/inspectionDate";
import {
  useBindInspectionStepSave,
  type BindInspectionStepSave,
} from "../../../utils/useBindInspectionStepSave";
import InspectionParticipantsStep from "./InspectionParticipantsStep";

export interface InspectionGeneralDataStepProps {
  structureId: string;
  detail: InspectionDetail;
  canWrite: boolean;
  onSaved?: (detail: InspectionDetail) => void;
  /** Y=11 — só a lista; não avança o step (isso é o Salvar/Avançar da data). */
  onParticipantsChange?: (participants: InspectionParticipant[]) => void;
  onBindSave?: BindInspectionStepSave;
}

/**
 * Step 1 da ficha — `generalData` (form §7.1). Não é avaliativo: nunca soma
 * anomalia. `inspectionCode` e responsável são somente leitura; a data só é
 * editável em inspeção **manual** ainda não finalizada (programada tem data
 * fixa da programação — quality-test QT-INSP-04-15).
 */
const InspectionGeneralDataStep = ({
  structureId,
  detail,
  canWrite,
  onSaved,
  onParticipantsChange,
  onBindSave,
}: InspectionGeneralDataStepProps) => {
  const { translate } = useLocalization();
  const [date, setDate] = useState(() =>
    toInspectionDateInputValue(detail.inspectionDate),
  );

  const { mutate: saveInspection } = useSaveInspectionMutation(
    structureId,
    detail.id,
  );

  const isDateEditable = canWrite && detail.creationOrigin === "manual";

  const handleSave = () =>
    saveInspection(
      isDateEditable ? { inspectionDate: date } : {},
      { onSuccess: (saved) => onSaved?.(saved) },
    );

  useBindInspectionStepSave(onBindSave, handleSave, canWrite);

  return (
    <div className="flex flex-col gap-4">
      <div className="insp-row-3">
        <div className="insp-field">
          <label htmlFor="inspection-general-code">
            {translate("inspection.form.generalData.code")}
          </label>
          <input
            id="inspection-general-code"
            readOnly
            value={String(detail.inspectionCode)}
            className="insp-inp readonly"
          />
        </div>

        <div className="insp-field">
          <label htmlFor="inspection-general-responsible">
            {translate("inspection.form.generalData.responsible")}
          </label>
          <input
            id="inspection-general-responsible"
            readOnly
            value={detail.responsibleName ?? ""}
            className="insp-inp readonly"
          />
        </div>

        <div className="insp-field">
          <label htmlFor="inspection-general-date">
            {translate("inspection.form.generalData.date")}
            {detail.creationOrigin === "scheduled"
              ? ` ${translate("inspection.form.generalData.dateScheduledSuffix")}`
              : ""}
          </label>
          <div className="insp-inp-icon">
            <input
              id="inspection-general-date"
              type="date"
              aria-label={
                detail.creationOrigin === "scheduled"
                  ? `${translate("inspection.form.generalData.date")} ${translate("inspection.form.generalData.dateScheduledSuffix")}`
                  : translate("inspection.form.generalData.date")
              }
              readOnly={!isDateEditable}
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className={`insp-inp${isDateEditable ? "" : " readonly"}`}
            />
            <Calendar aria-hidden size={16} className="insp-inp-icon-mark" />
          </div>
        </div>
      </div>

      <p className="insp-hint">{translate("inspection.form.extrasHint")}</p>

      <InspectionParticipantsStep
        structureId={structureId}
        inspectionId={detail.id}
        participants={detail.participants}
        canWrite={canWrite}
        inspectionStatus={detail.inspectionStatus}
        mode="manage"
        onSaved={onParticipantsChange}
      />

      {canWrite && !onBindSave ? (
        <button
          type="button"
          className="insp-btn insp-btn-primary self-end"
          onClick={handleSave}
        >
          {translate("inspection.form.saveAndContinue")}
        </button>
      ) : null}
    </div>
  );
};

export default InspectionGeneralDataStep;
